import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import Icon from '../../../components/AppIcon';

/**
 * TrendChart, 12-month pay gap trend with 5% threshold reference.
 * Uses brand teal gradient and a subtle area fill to give it more substance.
 */

const CustomTooltip = ({ active, payload, label, t }) => {
  if (!active || !payload?.length) return null;
  const overall = payload.find((p) => p.dataKey === 'overall')?.value;
  const median = payload.find((p) => p.dataKey === 'median')?.value;
  const breach = overall > 5;
  return (
    <div className="bg-popover border border-border rounded-md p-3 shadow-elevated min-w-[180px]">
      <p className="text-xs font-semibold text-popover-foreground mb-2">{label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-teal-600" />
            <span className="text-muted-foreground">{t('dashboard.trend.tooltipMean')}</span>
          </div>
          <span className={`font-semibold tabular-nums ${breach ? 'text-destructive' : 'text-foreground'}`}>
            {overall}%
          </span>
        </div>
        <div className="flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-slate-400" />
            <span className="text-muted-foreground">{t('dashboard.trend.tooltipMedian')}</span>
          </div>
          <span className="font-semibold tabular-nums text-foreground">{median}%</span>
        </div>
      </div>
      {breach && (
        <div className="mt-2 pt-2 border-t border-border flex items-center gap-1 text-[10px] text-destructive">
          <Icon name="AlertTriangle" size={10} />
          <span>{t('dashboard.trend.thresholdAbove')}</span>
        </div>
      )}
    </div>
  );
};

const TrendChart = ({ data, title }) => {
  const { t } = useTranslation();
  const { delta: fmtDelta } = useFormatters();
  const latest = data[data.length - 1];
  const earliest = data[0];
  const delta = +(latest.overall - earliest.overall).toFixed(1);
  const improving = delta < 0;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-1">{title || t('dashboard.trend.title')}</h3>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.trend.current')} <span className="font-semibold text-foreground">{latest.overall}%</span>
            {' · '}
            <span className={improving ? 'text-success' : 'text-destructive'}>
              {t('dashboard.trend.overPeriod', { delta: (delta > 0 ? '+' : '') + delta })}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-teal-600" />
            <span>{t('dashboard.trend.mean')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-400" />
            <span>{t('dashboard.trend.median')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-destructive" style={{ borderTop: '1px dashed #DC2626' }} />
            <span>{t('dashboard.trend.threshold')}</span>
          </div>
        </div>
      </div>

      <div className="p-2 pb-4">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#14B8A6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                width={30}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<CustomTooltip t={t} />} cursor={{ stroke: 'var(--color-border)', strokeWidth: 1 }} />
              {/* Danger zone, soft red band above 5% threshold */}
              <ReferenceArea y1={5} y2={999} fill="#DC2626" fillOpacity={0.04} />
              <ReferenceLine
                y={5}
                stroke="#DC2626"
                strokeDasharray="4 4"
                strokeWidth={1}
                label={{
                  value: t('dashboard.trend.danger'),
                  position: 'right',
                  fill: '#DC2626',
                  fontSize: 9,
                  fontWeight: 600,
                }}
              />
              <Area
                type="monotone"
                dataKey="overall"
                stroke="none"
                fill="url(#trendFill)"
              />
              <Line
                type="monotone"
                dataKey="overall"
                stroke="#0D9488"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: '#0D9488' }}
                name={t('dashboard.trend.tooltipMean')}
              />
              <Line
                type="monotone"
                dataKey="median"
                stroke="#94A3B8"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                activeDot={{ r: 3, fill: '#94A3B8' }}
                name={t('dashboard.trend.tooltipMedian')}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TrendChart;
